/**
 * Pure display + mapping helpers for the meal-plan screens. No I/O, so the
 * fiddly parts (the cooked-preview → mark-cooked request mapping in
 * particular) can be unit-tested on their own.
 */
import { formatQuantity, leftoverServings, nextDayIso } from '@fewerbags/core';
import { MEAL_SLOTS } from '@fewerbags/schema';
import type { cookedPreviewResponseSchema } from '@fewerbags/schema';
import type {
  MealPlanDto,
  MealSlot,
  RecipeIngredientDto,
  SuggestionDto,
  TimelineEntry,
  TimelineResponse,
  UnitKey,
} from '@fewerbags/schema';
import type { z } from 'zod';
import { displayAmount } from '../pages/recipes/recipe-form';
import { isIsoDate, isTimeOfDay } from './planDates';

export type CookedPreviewResponse = z.infer<typeof cookedPreviewResponseSchema>;
export type CookedProposal = CookedPreviewResponse['proposals'][number];
export type MealPlanStatus = MealPlanDto['status'];

export const SLOT_ORDER: readonly MealSlot[] = MEAL_SLOTS;

export const SLOT_LABEL: Record<MealSlot, string> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
  snack: 'Snack',
};

export const SLOT_OPTIONS = SLOT_ORDER.map((value) => ({ value, label: SLOT_LABEL[value] }));

export const STATUS_LABEL: Record<MealPlanStatus, string> = {
  planned: 'Planned',
  shopped: 'Shopped',
  cooked: 'Cooked',
  skipped: 'Skipped',
};

/** Badge styling per status — cooked is the only "done" state. */
export function statusBadgeClass(status: MealPlanStatus): string {
  switch (status) {
    case 'cooked':
      return 'bg-accent-soft text-accent-ink';
    case 'shopped':
      return 'bg-brand-soft text-brand';
    case 'skipped':
      return 'bg-cream text-muted';
    case 'planned':
    default:
      return 'bg-cream text-muted';
  }
}

/**
 * Household size used for new meals and recipe scaling. The API always sends
 * `defaultServings`; the constant only covers the moment before /auth/me has
 * loaded and mirrors the column default (`household.default_servings`).
 */
export const FALLBACK_SERVINGS = 4;
export function householdServings(
  household: { defaultServings?: number | null } | null | undefined,
): number {
  const n = household?.defaultServings;
  return typeof n === 'number' && n > 0 ? n : FALLBACK_SERVINGS;
}

/** True for a leftovers meal (PLAN §9 #7): it eats another plan's extra servings. */
export function isLeftovers(plan: Pick<MealPlanDto, 'leftoversOfPlanId'>): boolean {
  return plan.leftoversOfPlanId !== null && plan.leftoversOfPlanId !== undefined;
}

/** Tag shown on a leftovers meal in the week and month views. */
export const LEFTOVERS_LABEL = 'Leftovers';

/** Badge styling for the "Leftovers" tag. */
export const LEFTOVERS_BADGE_CLASS = 'bg-orange-soft text-orange-ink';

/**
 * Card headline when the card shows its own "Leftovers" tag: the source
 * meal's title for leftovers, the usual `planTitle` otherwise.
 */
export function planHeadline(plan: MealPlanDto): string {
  if (isLeftovers(plan)) return plan.leftoversOf?.title ?? 'Removed meal';
  return planTitle(plan);
}

/** Tag for a dine-out or packed meal (D32); null for an ordinary or leftovers meal. */
export function planKindLabel(
  plan: Pick<MealPlanDto, 'kind' | 'placeName' | 'dineOutNote'>,
): string | null {
  if (plan.kind === 'dine_out') return 'Dining out';
  if (plan.kind === 'packed') return plan.placeName ? `Packed · ${plan.placeName}` : 'Packed';
  return null;
}

/** One-word summary of a plan's recipes for a compact card. */
export function planTitle(plan: MealPlanDto): string {
  if (plan.leftoversOf) return `${LEFTOVERS_LABEL}: ${plan.leftoversOf.title}`;
  if (isLeftovers(plan)) return LEFTOVERS_LABEL;
  if (plan.kind === 'dine_out' && plan.recipes.length === 0) {
    return plan.dineOutNote ? `Dining out: ${plan.dineOutNote}` : 'Dining out';
  }
  if (plan.recipes.length === 0) return 'Nothing planned';
  return plan.recipes.map((r) => r.title).join(' + ');
}

export interface LeftoversOffer {
  servings: number;
  date: string;
  slot: MealSlot;
}

/**
 * Whether to offer "Save N servings as leftovers" for a plan, and the default
 * target (the next day, same slot). Offered when the scaled recipes make more
 * whole servings than the people eating this meal (`plan.servings`, which
 * starts at the household size), the plan is not itself leftovers, and none
 * of the `nearby` plans (the following week) is already its leftovers.
 */
export function leftoversOffer(
  plan: MealPlanDto,
  nearby: readonly MealPlanDto[] = [],
): LeftoversOffer | null {
  if (isLeftovers(plan) || plan.status === 'skipped') return null;
  // Already saved (a leftovers meal in the nearby window points back here).
  if (nearby.some((p) => p.leftoversOfPlanId === plan.id)) return null;
  const servings = leftoverServings(
    plan.recipes.map((r) => ({ servings: r.servings, scale: r.scale })),
    plan.servings,
  );
  if (servings <= 0) return null;
  return { servings: Math.min(50, servings), date: nextDayIso(plan.date), slot: plan.slot };
}

/** Toast after marking a meal cooked, given what happened to the leftovers. */
export function cookedToast(leftovers: 'none' | 'saved' | 'failed'): string {
  if (leftovers === 'saved') return 'Marked cooked, pantry updated and leftovers saved.';
  if (leftovers === 'failed')
    return 'Marked cooked and the pantry is updated. The leftovers could not be saved; add them from the meal.';
  return 'Marked cooked and the pantry is updated.';
}

/** Window searched for an existing leftovers meal: the plan's day and the week after. */
export function leftoversWindow(plan: Pick<MealPlanDto, 'date'>): { from: string; to: string } {
  let to = plan.date;
  for (let i = 0; i < 7; i++) to = nextDayIso(to);
  return { from: plan.date, to };
}

/** "Save 4 servings as leftovers for Tuesday lunch". */
export function leftoversOfferLabel(
  offer: LeftoversOffer,
  formatDay: (iso: string) => string,
): string {
  const noun = offer.servings === 1 ? 'serving' : 'servings';
  return `Save ${offer.servings} ${noun} as leftovers for ${formatDay(offer.date)} ${SLOT_LABEL[
    offer.slot
  ].toLowerCase()}`;
}

/** Plans for one day+slot, in a stable order (earliest serve time first). */
export function plansFor(
  plans: readonly MealPlanDto[],
  date: string,
  slot: MealSlot,
): MealPlanDto[] {
  return plans
    .filter((p) => p.date === date && p.slot === slot)
    .sort((a, b) => (a.serveAt ?? '').localeCompare(b.serveAt ?? '') || a.id.localeCompare(b.id));
}

/** Slots that actually hold something, for the compact phone list. */
export function usedSlots(plans: readonly MealPlanDto[], date: string): MealSlot[] {
  return SLOT_ORDER.filter((slot) => plans.some((p) => p.date === date && p.slot === slot));
}

/** The plans a household should see next: today onwards, soonest first. */
export function upcomingPlans(
  plans: readonly MealPlanDto[],
  fromDate: string,
  limit = 5,
): MealPlanDto[] {
  return plans
    .filter((p) => p.date >= fromDate && p.status !== 'skipped')
    .sort(
      (a, b) =>
        a.date.localeCompare(b.date) ||
        SLOT_ORDER.indexOf(a.slot) - SLOT_ORDER.indexOf(b.slot) ||
        (a.serveAt ?? '').localeCompare(b.serveAt ?? ''),
    )
    .slice(0, limit);
}

/**
 * Smallest colour index not already used on a plan, and the default scale for
 * a newly added recipe (plan servings / recipe servings). Both mirror the
 * API's own assignment (`apps/api/src/routes/plans/service.ts`) so an
 * optimistic "add a recipe" update matches what the server will return.
 */
export function nextColorIndex(used: Iterable<number>): number {
  const set = new Set(used);
  for (let i = 0; ; i++) {
    if (!set.has(i)) return i;
  }
}

export function defaultScale(planServings: number, recipeServings: number | null): number {
  if (!recipeServings || recipeServings <= 0) return 1;
  return Math.round((planServings / recipeServings) * 1000) / 1000;
}

/**
 * A recipe's scale after the plan's servings change — mirrors the API's
 * `rescaleForServings` so the optimistic update matches the server: a
 * recipe at its default scale gets the new default, a hand-set one keeps its
 * proportion (scale × new/old), clamped to 0.01–20.
 */
export function rescaleForServings(
  scale: number,
  recipeServings: number | null,
  oldServings: number,
  newServings: number,
): number {
  let next: number;
  if (recipeServings && recipeServings > 0 && scale === defaultScale(oldServings, recipeServings)) {
    next = defaultScale(newServings, recipeServings);
  } else {
    next = Math.round(((scale * newServings) / Math.max(1, oldServings)) * 1000) / 1000;
  }
  return Math.min(20, Math.max(0.01, next));
}

/** Scale a recipe's own servings up to the plan's ("×1.5", "as written"). */
export function scaleLabel(scale: number): string {
  if (Math.abs(scale - 1) < 0.005) return 'as written';
  return `×${scale.toFixed(2).replace(/\.?0+$/, '')}`;
}

/** Chip styling for a suggestion reason: positive reasons read green, penalties muted. */
export function reasonChipClass(delta: number): string {
  if (delta > 0) return 'bg-accent-soft text-accent-ink';
  if (delta < 0) return 'bg-orange-soft text-orange-ink';
  return 'bg-brand-soft text-brand';
}

/** Reasons worth showing as chips — the strongest first, penalties last. */
export function topReasons(suggestion: SuggestionDto, limit = 3): SuggestionDto['reasons'] {
  return [...suggestion.reasons].sort((a, b) => b.delta - a.delta).slice(0, limit);
}

/** Timeline entries that have not finished yet, at `now` (ISO datetimes). */
export function splitTimeline(entries: readonly TimelineEntry[], nowIso: string) {
  const now = new Date(nowIso).getTime();
  const done: TimelineEntry[] = [];
  const current: TimelineEntry[] = [];
  const upcoming: TimelineEntry[] = [];
  for (const e of entries) {
    const start = new Date(e.start).getTime();
    const end = new Date(e.end).getTime();
    if (end <= now) done.push(e);
    else if (start <= now) current.push(e);
    else upcoming.push(e);
  }
  return { done, current, upcoming };
}

// --------------------------------------------------- plan header auto-save --

/** The plan editor's header fields: what auto-saves as the person edits them. */
export interface PlanHeaderDraft {
  date: string;
  slot: MealSlot;
  /** `HH:MM`, or '' for "no serve time". */
  serveAt: string;
  servings: number;
  /** D32. `leftovers` is derived and never sent. */
  kind: MealPlanDto['kind'];
  /** Meal place id, or '' for home. */
  placeId: string;
  /** '' = no note. */
  dineOutNote: string;
}

export const DINE_OUT_NOTE_MAX = 200;

type HeaderSource = Pick<MealPlanDto, 'date' | 'slot' | 'serveAt' | 'servings'> &
  Partial<Pick<MealPlanDto, 'kind' | 'placeId' | 'dineOutNote'>>;

export function headerOf(plan: HeaderSource) {
  return {
    date: plan.date,
    slot: plan.slot,
    serveAt: plan.serveAt ?? '',
    servings: plan.servings,
    kind: plan.kind ?? 'cook',
    placeId: plan.placeId ?? '',
    dineOutNote: plan.dineOutNote ?? '',
  } satisfies PlanHeaderDraft;
}

export function sameHeader(a: PlanHeaderDraft, b: PlanHeaderDraft): boolean {
  return (
    a.date === b.date &&
    a.slot === b.slot &&
    a.serveAt === b.serveAt &&
    a.servings === b.servings &&
    a.kind === b.kind &&
    a.placeId === b.placeId &&
    // The API trims the note; compare what would be stored.
    a.dineOutNote.trim() === b.dineOutNote.trim()
  );
}

/** Whether a draft can be sent as is (a half-typed date or time cannot). */
export function isValidHeader(d: PlanHeaderDraft): boolean {
  return (
    isIsoDate(d.date) &&
    (d.serveAt === '' || isTimeOfDay(d.serveAt)) &&
    Number.isInteger(d.servings) &&
    d.servings >= 1 &&
    d.servings <= 50 &&
    d.dineOutNote.trim().length <= DINE_OUT_NOTE_MAX
  );
}

/**
 * PATCH body for a header draft. Date, slot, serve time and servings are
 * always sent (as before); kind, place and the dine-out note only when they
 * changed and the plan is not a leftovers meal (which takes none of them).
 */
export function headerPatch(saved: PlanHeaderDraft, next: PlanHeaderDraft, leftovers: boolean) {
  const out: {
    date: string;
    slot: MealSlot;
    serveAt: string | null;
    servings: number;
    kind?: 'cook' | 'dine_out' | 'packed';
    placeId?: string | null;
    dineOutNote?: string | null;
  } = {
    date: next.date,
    slot: next.slot,
    serveAt: next.serveAt ? next.serveAt : null,
    servings: next.servings,
  };
  if (leftovers) return out;
  if (next.kind !== saved.kind && next.kind !== 'leftovers') out.kind = next.kind;
  if (next.placeId !== saved.placeId) out.placeId = next.placeId || null;
  const note = next.dineOutNote.trim();
  if (note !== saved.dineOutNote.trim()) out.dineOutNote = note || null;
  return out;
}

/** Kinds a person can pick for a plan; dining out only once the recipes are gone. */
export function planKindOptions(
  current: MealPlanDto['kind'],
  recipeCount: number,
): Array<{ value: string; label: string }> {
  const out = [
    { value: 'cook', label: 'Cooked meal' },
    { value: 'packed', label: 'Packed meal' },
  ];
  if (recipeCount === 0 || current === 'dine_out')
    out.push({ value: 'dine_out', label: 'Dining out' });
  return out;
}

// ------------------------------------------------------ cooking ingredients --

export interface CookIngredientLine {
  id: string;
  /** "1 ½ cups", "to taste", or '' when the line has no amount. */
  amount: string;
  name: string;
  prepNote: string | null;
  optional: boolean;
}

/**
 * A recipe's ingredient lines as cooking mode lists them: in recipe order and
 * multiplied by the plan recipe's `scale` (the same factor the timeline, the
 * grocery list and the pantry decrement use).
 */
export function cookIngredientLines(
  ingredients: readonly RecipeIngredientDto[],
  scale: number,
  system: 'us' | 'metric' = 'us',
): CookIngredientLine[] {
  const factor = Number.isFinite(scale) && scale > 0 ? scale : 1;
  return [...ingredients]
    .sort((a, b) => a.sort - b.sort)
    .map((line) => ({
      id: line.id,
      amount: displayAmount(line.qty, line.qtyMax, line.unit, factor, system),
      name: line.name,
      prepNote: line.prepNote,
      optional: line.optional,
    }));
}

const COUNTER_PREFIX = 'fewerbags:cook-counter:';

export function counterStorageKey(planId: string): string {
  return `${COUNTER_PREFIX}${planId}`;
}

/** Ingredient lines ticked "out on the counter" for a plan (survives a reload, like the timers). */
export function loadCounterTicks(planId: string): string[] {
  if (typeof sessionStorage === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(counterStorageKey(planId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export function saveCounterTicks(planId: string, ticks: readonly string[]): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(counterStorageKey(planId), JSON.stringify(ticks));
  } catch {
    // Private mode / quota — the ticks simply do not survive a reload.
  }
}

/** Tick or untick one line (returns a new list). */
export function toggleTick(ticks: readonly string[], id: string): string[] {
  return ticks.includes(id) ? ticks.filter((t) => t !== id) : [...ticks, id];
}

// ------------------------------------------------------- running late shift --

function shiftIso(iso: string, deltaMs: number): string {
  return new Date(new Date(iso).getTime() + deltaMs).toISOString();
}

/**
 * Every timestamp in a timeline moved by `deltaMs` (0 returns the same object).
 * Mirrors the mobile app's `shiftTimeline`
 * (`apps/mobile/src/features/cooking/reanchor.ts`); not shared across apps.
 */
export function shiftTimeline(timeline: TimelineResponse, deltaMs: number): TimelineResponse {
  if (deltaMs === 0) return timeline;
  return {
    ...timeline,
    serveAt: shiftIso(timeline.serveAt, deltaMs),
    startAt: shiftIso(timeline.startAt, deltaMs),
    entries: timeline.entries.map((entry) => ({
      ...entry,
      start: shiftIso(entry.start, deltaMs),
      end: shiftIso(entry.end, deltaMs),
    })),
    conflicts: timeline.conflicts.map((conflict) => ({
      ...conflict,
      at: shiftIso(conflict.at, deltaMs),
    })),
  };
}

/**
 * "I'm running late — re-plan from now". The API re-runs the scheduler with
 * `now` but only *reports* a `too_late` conflict when cooking should already
 * have started; this moves the whole schedule (serve time included) later so
 * it starts at `anchorIso`. The `too_late` conflict is dropped — the shift is
 * its fix — and the plan is feasible again unless another conflict remains
 * (the scheduler marks every conflict it reports as infeasible). Returns
 * `shiftedMs = 0` and the timeline untouched when there is nothing to move.
 */
export function reanchorLate(
  timeline: TimelineResponse,
  anchorIso: string | null | undefined,
): { timeline: TimelineResponse; shiftedMs: number } {
  if (!anchorIso) return { timeline, shiftedMs: 0 };
  const anchor = new Date(anchorIso).getTime();
  const start = new Date(timeline.startAt).getTime();
  if (!Number.isFinite(anchor) || !Number.isFinite(start) || start >= anchor) {
    return { timeline, shiftedMs: 0 };
  }
  const shiftedMs = anchor - start;
  const shifted = shiftTimeline(timeline, shiftedMs);
  const conflicts = shifted.conflicts.filter((c) => c.kind !== 'too_late');
  return {
    timeline: { ...shifted, conflicts, feasible: conflicts.length === 0 },
    shiftedMs,
  };
}

// ------------------------------------------------- cooked preview → request --

/** One row of the mark-cooked dialog: the proposal plus what the person edited. */
export interface CookedSelection {
  pantryItemId: string;
  ingredientName: string;
  /** Quantity as typed, so nothing is silently reformatted while editing. */
  qtyText: string;
  unit: UnitKey;
  checked: boolean;
  availableQty: number;
  needQty: number | null;
  note: string | undefined;
  /** The linked product ("Black beans, 15 oz can"), when the pantry row has one. */
  productLabel: string | null;
  /** "Discard the rest" (D35 F3): use the whole row up, the remainder is thrown away. */
  discardRemainder: boolean;
  /** Why the API suggested discarding ("Opened cans don't keep"); null when it did not. */
  discardReason: string | null;
}

function trimNumber(value: number): string {
  return String(Math.round(value * 1000) / 1000);
}

/**
 * Initial dialog rows from the preview. A proposal the API could not convert
 * (`consumeQty === null`) starts unchecked with an empty amount — the person
 * decides — and anything at zero starts unchecked too.
 */
export function initialCookedSelections(preview: CookedPreviewResponse): CookedSelection[] {
  return preview.proposals.map((p) => ({
    pantryItemId: p.pantryItemId,
    ingredientName: p.ingredientName,
    qtyText: p.consumeQty === null || p.consumeQty <= 0 ? '' : trimNumber(p.consumeQty),
    unit: p.unit,
    checked: p.consumeQty !== null && p.consumeQty > 0,
    availableQty: p.availableQty,
    needQty: p.needQty,
    note: p.note,
    productLabel: p.productLabel,
    discardRemainder: p.suggestDiscard,
    discardReason: p.discardReason,
  }));
}

/** The amount "discard the rest" would throw away for a row; null when it would discard nothing. */
export function discardedRemainder(s: CookedSelection): number | null {
  if (!s.checked || !s.discardRemainder) return null;
  const qty = Number(s.qtyText);
  if (s.qtyText.trim() === '' || !Number.isFinite(qty) || qty <= 0) return null;
  const rest = s.availableQty - qty;
  return rest > 1e-9 ? rest : null;
}

/**
 * Helper text under the "Discard the rest" checkbox, e.g.
 * "Opened cans don't keep · 6 oz would be thrown out". Null when there is
 * nothing to say (no reason and nothing would be discarded).
 */
export function discardHelpText(s: CookedSelection): string | null {
  const rest = discardedRemainder(s);
  const parts: string[] = [];
  if (s.discardReason) parts.push(s.discardReason);
  if (rest !== null) parts.push(`${formatQuantity(rest, s.unit)} would be thrown out`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** Confirm button: "Mark cooked", "Mark cooked · use 3 items", "… · use 3 items, discard 1 remainder". */
export function cookedConfirmLabel(selections: readonly CookedSelection[]): string {
  const { consume } = toMarkCookedRequest(selections);
  if (consume.length === 0) return 'Mark cooked';
  const use = `use ${consume.length} ${consume.length === 1 ? 'item' : 'items'}`;
  const discards = selections.filter((s) => discardedRemainder(s) !== null).length;
  if (discards === 0) return `Mark cooked · ${use}`;
  return `Mark cooked · ${use}, discard ${discards} ${discards === 1 ? 'remainder' : 'remainders'}`;
}

export interface CookedSelectionIssue {
  pantryItemId: string;
  message: string;
}

/** Per-row validation shown inline: a ticked row needs a positive amount it has stock for. */
export function cookedSelectionIssues(
  selections: readonly CookedSelection[],
): CookedSelectionIssue[] {
  const issues: CookedSelectionIssue[] = [];
  for (const s of selections) {
    if (!s.checked) continue;
    const qty = Number(s.qtyText);
    if (s.qtyText.trim() === '' || !Number.isFinite(qty) || qty <= 0) {
      issues.push({ pantryItemId: s.pantryItemId, message: 'Enter an amount above zero.' });
    } else if (qty > s.availableQty) {
      issues.push({
        pantryItemId: s.pantryItemId,
        message: `Only ${trimNumber(s.availableQty)} on hand.`,
      });
    }
  }
  return issues;
}

export interface CookedConsume {
  pantryItemId: string;
  qty: number;
  unit: UnitKey;
  /** Only sent when true; the API defaults it to false. */
  discardRemainder?: true;
}

/**
 * Dialog rows → the POST body. Unticked rows and rows without a usable amount
 * are dropped, so confirming with everything unticked marks the plan cooked
 * without touching the pantry (which the contract allows). A row with
 * "discard the rest" carries `discardRemainder: true` so the API uses the
 * whole row up and records the remainder as waste.
 */
export function toMarkCookedRequest(selections: readonly CookedSelection[]): {
  consume: CookedConsume[];
} {
  const consume: CookedConsume[] = [];
  for (const s of selections) {
    if (!s.checked) continue;
    const qty = Number(s.qtyText);
    if (!Number.isFinite(qty) || qty <= 0) continue;
    consume.push({
      pantryItemId: s.pantryItemId,
      qty: Math.min(qty, s.availableQty),
      unit: s.unit,
      ...(s.discardRemainder ? { discardRemainder: true } : {}),
    });
  }
  return { consume };
}
